import { Injectable, computed, effect, signal, type Signal } from '@angular/core';

import type { ResolvedTheme, Theme } from './theme.types.js';

const STORAGE_KEY = 'echospaces-theme';
const VALID_THEMES: ReadonlySet<Theme> = new Set(['light', 'dark', 'system']);

/**
 * Manages the app's light/dark/system theme choice.
 *
 * - `theme` is what the user picked (persisted in localStorage).
 * - `resolvedTheme` is what actually renders — resolves `'system'` against
 *   the OS preference and re-computes when the OS pref flips.
 * - `isDark` / `darkMode` are shortcuts for templates.
 *
 * First-launch default is `'system'` so the app follows the OS out of the
 * box. `toggle()` flips between light and dark only (skipping `'system'`)
 * — matches the single-button UX in the header.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly systemDark: Signal<boolean>;
  private readonly _theme = signal<Theme>(this.readStored());

  /**
   * The user's raw choice. Use this for settings UIs.
   */
  readonly theme: Signal<Theme> = this._theme.asReadonly();

  /**
   * The concrete theme applied to the DOM after resolving `'system'`.
   */
  readonly resolvedTheme: Signal<ResolvedTheme>;

  /**
   * True when the resolved theme is dark.
   */
  readonly isDark: Signal<boolean>;

  /**
   * Backwards-compatible alias for `isDark`. Kept so callers migrating from
   * the old es-shared-ui ThemeService don't need to update templates.
   */
  readonly darkMode: Signal<boolean>;

  constructor() {
    this.systemDark = this.watchSystemDark();

    this.resolvedTheme = computed(() => {
      const t = this._theme();
      if (t === 'system') return this.systemDark() ? 'dark' : 'light';
      return t;
    });
    this.isDark = computed(() => this.resolvedTheme() === 'dark');
    this.darkMode = this.isDark;

    // Apply the class + persist on every change. `.dark` drives the Tailwind
    // apps; `data-theme` drives the es-ui-next token themes.
    effect(() => {
      const resolved = this.resolvedTheme();
      if (typeof document !== 'undefined') {
        document.documentElement.classList.toggle('dark', resolved === 'dark');
        document.documentElement.dataset['theme'] = resolved;
      }
      this.writeStored(this._theme());
    });
  }

  /**
   * Set the user's preference. No-op for invalid values.
   */
  setTheme(theme: Theme): void {
    if (!VALID_THEMES.has(theme)) return;
    this._theme.set(theme);
  }

  /**
   * Flip between light and dark. Ignores `'system'` — falls back to the
   * opposite of whatever is currently resolved.
   */
  toggle(): void {
    this.setTheme(this.isDark() ? 'light' : 'dark');
  }

  private readStored(): Theme {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      return raw && VALID_THEMES.has(raw as Theme) ? (raw as Theme) : 'system';
    } catch {
      return 'system';
    }
  }

  private writeStored(theme: Theme): void {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Quota / SSR / private mode — silent ignore.
    }
  }

  private watchSystemDark(): Signal<boolean> {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return signal(false).asReadonly();
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const state = signal(mql.matches);
    const listener = (e: MediaQueryListEvent) => state.set(e.matches);
    mql.addEventListener('change', listener);
    return state.asReadonly();
  }
}
